import { WWW_ROUTES } from '@root/root/routes';

import { reportError } from './monitoring';

const resolveIsLanding = () => location.hostname.includes('storage');

const HELP_REGEX = /^\/help/;
const CHECKOUT_STEP_REGEX = /^\/checkout\/steps\/(?<step>\w+)/;
const SEO_PAGE_REGEX = /^\/locations\/([^\/]+)/;

const LEGACY_PATH_TO_REGEX: Record<string, RegExp> = {
  local_self_storage_detail: /^\/self-storage/,
  local_region: /^\/loc\/(\S+)\/storage$/,
  local_city: /^\/loc\/(\S+)\/storage-units$/,
};

const getLegacyPageName = (pathname: string) => {
  for (const key in LEGACY_PATH_TO_REGEX) {
    if (LEGACY_PATH_TO_REGEX[key].test(pathname)) {
      return key;
    }
  }
};

const getPageNameFromPath = (pathname: string) => {
  let match: RegExpMatchArray | null;
  if (HELP_REGEX.test(pathname)) return 'help';
  if (pathname.startsWith('/p/')) return 'landing_page';
  if ((match = pathname.match(CHECKOUT_STEP_REGEX))) {
    const step = match.groups!.step;
    return `checkout_step_${step}`;
  }
  if ((match = pathname.match(SEO_PAGE_REGEX))) {
    return `seo_page`;
  }

  if ((match = pathname.match(/^\/cr\/(\S+)/))) {
    return `callback_request_edit`;
  }

  switch (pathname) {
    case '':
    case '/':
      return 'home';
    case '/locations':
      return 'locations';
    case '/partnerships':
      return 'partnerships';
    case '/legal':
      return 'legal';
    case '/legal/tos':
      return 'legal';
    case '/legal/privacy_policy':
      return 'legal';
    case '/legal/california_privacy_rights':
      return 'legal';
    case '/legal/limited_security_warranty':
      return 'legal';
    case '/getstarted':
      return 'checkout';
    case '/guide':
      return 'guide';
    case WWW_ROUTES.STORAGE_CART:
      return 'smart_storage_cart';
    case WWW_ROUTES.STORAGE_SET_PASSWORD:
      return 'smart_storage_set_password';
    case WWW_ROUTES.MOVING_CART:
      return 'moving_cart';
    case WWW_ROUTES.PRODUCT_PAGE_STORAGE:
      return 'smart_storage_detail_page';
    case WWW_ROUTES.PRODUCT_PAGE_MOVING:
      return 'moving_detail_page';
  }
};

const MISSING_PAGENAME_MESSAGE =
  'Unable to generate landing page name: Invalid pathname';

interface IPageNameOptions {
  prefix?: boolean;
}

export const getPageName = ({ prefix = true }: IPageNameOptions = {}) => {
  if (typeof window === 'undefined') {
    return 'unknown';
  }

  const pathname = window.location.pathname.replace(/\/$/, ''); // Ensures no trailing slash
  const isLanding = resolveIsLanding();
  let resolvedPrefix = '';
  if (prefix) {
    resolvedPrefix = isLanding ? 'landing:' : 'www:';
  }
  const resolvedPageName = getPageNameFromPath(pathname);

  if (!resolvedPageName) {
    const legacyPageName = getLegacyPageName(pathname);
    // These page names do not conform to the newer prefixed pattern
    if (legacyPageName) return legacyPageName;
  }

  if (resolvedPageName) {
    return `${resolvedPrefix}${resolvedPageName}`;
  } else if (process.env.NODE_ENV === 'production') {
    reportError(new Error(MISSING_PAGENAME_MESSAGE));
    return `${resolvedPrefix}unknown`;
  } else {
    throw new Error(MISSING_PAGENAME_MESSAGE);
  }
};
